import React, { forwardRef } from 'react';
import moment from 'moment';
import { useTranslation, Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ImmutableRootState } from '@uncinc/react-drupal-core/types';
import useRoutes from '../../../hooks/useRoutes';

import { PROGRAM_DURATION_WEEKS } from '../../../config/constants';
import formatDate from '../../../helpers/formatDate';
import useClasses from '../../../hooks/useClasses';
import Kebab from '../../Kebab';
import useFocusWithArrowKeys from '../../../hooks/useFocusWithArrowKeys';
import { Registrations } from '../../../types';
import BarGraphStaff from '../BarGraphStaff';
import SkeletonWrapper from '../../SkeletonWrapper';

import './style.scss';

interface Props {
  // 1-indexed week number
  weekNumber?: number;
  highestAmount?: number;
  registrations: Registrations;
  hideGoalMessage?: boolean;
  readonly?: boolean;
  isLoading?: boolean;
  isCurrentWeek?: boolean;
  currentWeekNumber: number;
  hideTitle?: boolean;
}

const BarGraphWeekOverviewWeek = forwardRef<HTMLDivElement, Props>(({
  weekNumber,
  highestAmount,
  registrations,
  hideGoalMessage,
  readonly,
  isLoading,
  currentWeekNumber,
  isCurrentWeek = false,
  hideTitle = false,
}, ref) => {
  const { t } = useTranslation();
  const { buildClass } = useClasses('BarGraphWeekOverview');
  const dateKeys = Object.keys(registrations);
  const { changeRecoveryPlan } = useRoutes();

  const currentDomain = useSelector<ImmutableRootState<string>, string>(
    (state) => state.getIn(['uiInternal', 'currentDomain', 'raw'], null),
  );

  const isAfterCurrentWeek = weekNumber != null
    ? weekNumber > currentWeekNumber
    : false;

  const goalToday = React.useMemo(() => {
    const today = dateKeys.find((date) => moment().isSame(date, 'day'));

    if (!today) return null;

    return registrations[today].goal;
  }, [registrations, dateKeys]);

  const [firstDateFormatted, lastDateFormatted] = React.useMemo(() => {
    if (!dateKeys.length) return [null, null];

    const firstDate = moment(dateKeys[0]);
    const lastDate = moment(dateKeys[dateKeys.length - 1]);
    const dateFormat = 'D MMM';

    return [firstDate.format(dateFormat), lastDate.format(dateFormat)] as const;
  }, [registrations, dateKeys]);

  const staffContainerRef = React.useRef<HTMLOListElement>(null);
  useFocusWithArrowKeys(staffContainerRef);

  if (isLoading) {
    return (
      <div className={buildClass()}>
        {[...Array(7).keys()].map((item) => (
          <SkeletonWrapper
            key={`week-overview-${item}-skeleton`}
            active={isLoading}
            className={buildClass('skeleton')}
          />
        ))}
      </div>
    );
  }

  return (
    <div ref={isCurrentWeek ? ref : null} className={buildClass()}>
      <div className={buildClass('header')}>
        {isAfterCurrentWeek && (
          <div className={buildClass('kebab')}>
            <Kebab iconVariant={0} label={t('pages.recovery_plan.change_plan')}>
              <a className={buildClass('link')} href={`${changeRecoveryPlan}?week=${weekNumber}`}>
                <span>{t('pages.recovery_plan.change_plan')}</span>
                <i className="icon-before icon-writing" />
              </a>
            </Kebab>
          </div>
        )}

        {!hideTitle && weekNumber != null && (
          <h2
            className={buildClass('week-number')}
            id={isCurrentWeek ? 'BarGraphWeekOverview--current-week' : undefined}
          >
            {weekNumber > PROGRAM_DURATION_WEEKS
              ? t('pages.recovery_plan.week', { weekNumber })
              : t(`cta.week_numbers.${weekNumber}`)}
          </h2>
        )}

        {Boolean(goalToday) &&
          !hideGoalMessage &&
          weekNumber != null &&
          weekNumber <= PROGRAM_DURATION_WEEKS && (
            <p>
              <Trans
                i18nKey={[`cta.started.${currentDomain}`, 'cta.started.default']}
                values={{
                  week: t(`cta.week_numbers.${weekNumber}`).toLowerCase(),
                  day: formatDate(new Date(), 'dddd'),
                  verb: t(`cta.verbs.${currentDomain}`).toLowerCase(),
                  amount: t(`cta.units.${currentDomain}`, {
                    count: goalToday,
                  }),
                }}
              />
            </p>
        )}

        <div className={buildClass('date-range')}>
          {firstDateFormatted} - {lastDateFormatted}
        </div>
      </div>

      <ol className={buildClass('list')} ref={staffContainerRef}>
        {dateKeys.map((date) => (
          <li key={`registration_overview-${date}`} className={buildClass('list-item')}>
            <BarGraphStaff
              date={new Date(date)}
              max={highestAmount}
              value={registrations[date].totalAmount}
              goal={registrations[date].goal}
              readonly={readonly}
            />
          </li>
        ))}
      </ol>
    </div>
  );
});

export default BarGraphWeekOverviewWeek;
